import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "@/lib/api";
import { Section } from "@/components/intel/Section";
import { ItemCardEmpty } from "@/components/intel/ItemCard";
import { BriefingRenderer } from "@/components/intel/BriefingRenderer";
import { AddToCollectionMenu } from "@/components/collections/AddToCollectionMenu";

interface Briefing {
  id: string;
  kind: string;
  title: string;
  body_md: string | null;
  body_json: string | null;
  generated_at: number;
}

/**
 * Briefings — archive of past daily briefs. The active morning brief lives on
 * Intelligence (the dashboard). This page is the historical record only;
 * generation is auto-handled by the dashboard once per day.
 */
export function Briefings() {
  const api = useApi();
  const [openId, setOpenId] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ["briefings-archive"],
    queryFn: () => api.get<{ briefings: Briefing[] }>("/briefings?limit=30"),
  });

  const briefings = list.data?.briefings ?? [];
  const open = briefings.find((b) => b.id === openId) ?? briefings[0] ?? null;

  return (
    <div className="px-10 py-10 max-w-5xl">
      <header className="mb-12">
        <p className="label-eyebrow">Archive</p>
        <h1 className="mt-2 text-display-2 font-display">Briefings</h1>
        <p className="mt-3 text-whisper max-w-xl leading-relaxed">
          The history of your generated daily briefs. Today's lives on Intelligence;
          everything before lives here.
        </p>
      </header>

      {list.isLoading ? (
        <ItemCardEmpty label="Reading the archive…" />
      ) : briefings.length === 0 ? (
        <ItemCardEmpty
          label="No briefings yet."
          detail="Your daily brief is generated automatically when you open Intelligence. Once one is generated, it lands here."
        />
      ) : (
        <div className="grid grid-cols-[260px_1fr] gap-10">
          <aside>
            <p className="label-eyebrow mb-3">All briefs</p>
            <ul className="space-y-1">
              {briefings.map((b) => {
                const isActive = open?.id === b.id;
                return (
                  <li key={b.id}>
                    <button
                      type="button"
                      onClick={() => setOpenId(b.id)}
                      className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                        isActive
                          ? "bg-veil/60 text-fog"
                          : "text-whisper hover:bg-veil/30 hover:text-fog"
                      }`}
                    >
                      <div className="text-[10px] uppercase tracking-[0.18em] text-muted">
                        {new Date(b.generated_at).toLocaleDateString(undefined, {
                          weekday: "short",
                          month: "short",
                          day: "numeric",
                        })}
                      </div>
                      <div className="mt-0.5 text-xs leading-snug">
                        {b.kind === "daily" ? "Daily brief" : b.title}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>

          <Section
            eyebrow={
              open
                ? new Date(open.generated_at).toLocaleString(undefined, {
                    dateStyle: "long",
                    timeStyle: "short",
                  })
                : ""
            }
            title={open?.title ?? ""}
          >
            {open && (
              <article className="panel p-10 animate-fade-up">
                <div className="flex justify-end -mt-2 mb-4">
                  <AddToCollectionMenu kind="briefing" entryId={open.id} variant="labeled" />
                </div>
                <BriefingRenderer body_json={open.body_json} body_md={open.body_md} />
              </article>
            )}
          </Section>
        </div>
      )}
    </div>
  );
}
